import type {
  ProposalCollaborationEnvelope,
  ProposalConflict,
  ProposalContentOperation,
  ProposalReviewerChange,
  SaveProposalDraftCommand,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  proposalConflictKeyContains,
  proposalResolutionDecisionTargetsState,
} from "work/proposals/negotiation/proposal-operation-containment";
import {
  canonicalProposalCollectionItem,
  proposalStrictJsonValuesEqual,
} from "work/proposals/negotiation/proposal-collection-codec";
import {
  normalizeProposalClientTeamRoster,
  proposalClientTeamRostersAreEqual,
  rebaseProposalClientTeamRoster,
  type ProposalClientTeamMemberReference,
  type ProposalClientTeamRosterDraft,
} from "work/proposals/negotiation/proposal-client-team-roster";

export type InFlightProposalCommand = SaveProposalDraftCommand;

export type ProposalCollaborationSyncStatus = "idle" | "saving" | "resyncRequired" | "error";

export type ProposalCollaborationState = {
  envelope?: ProposalCollaborationEnvelope;
  pendingOperations: ProposalContentOperation[];
  pendingReviewerChanges: ProposalReviewerChange[];
  /**
   * The client-team roster this viewer has edited but not yet saved.
   *
   * It sits beside the operation queue rather than in it: client-team membership is reference
   * state written by its own command, so it never becomes a content operation (see
   * `docs/design/proposals-negotiation/ARCHITECTURE.md`). It waits for the same explicit Save all
   * the same (#5116), which is why it is held on the entry that Save reads.
   */
  pendingClientTeamMembers?: ProposalClientTeamRosterDraft;
  inFlightCommands: Record<string, InFlightProposalCommand | undefined>;
  conflicts: ProposalConflict[];
  syncStatus: ProposalCollaborationSyncStatus;
};

export function createProposalCollaborationState(
  initial?: Partial<ProposalCollaborationState>,
): ProposalCollaborationState {
  return {
    envelope: initial?.envelope,
    pendingOperations: initial?.pendingOperations ?? [],
    pendingReviewerChanges: initial?.pendingReviewerChanges ?? [],
    pendingClientTeamMembers: initial?.pendingClientTeamMembers,
    inFlightCommands: initial?.inFlightCommands ?? {},
    conflicts: initial?.conflicts ?? [],
    syncStatus: initial?.syncStatus ?? "idle",
  };
}

export function receiveProposalEnvelope(
  state: ProposalCollaborationState,
  envelope: ProposalCollaborationEnvelope,
): ProposalCollaborationState {
  if (state.envelope && envelope.streamVersion < state.envelope.streamVersion) return state;

  const acknowledgedCommandIds = new Set(envelope.acknowledgedCommandIds);
  const acknowledgedOperationIds = new Set<string>();
  const inFlightCommands = { ...state.inFlightCommands };

  for (const commandId of acknowledgedCommandIds) {
    const command = inFlightCommands[commandId];
    command?.operations.forEach((operation) => acknowledgedOperationIds.add(operation.operationId));
    delete inFlightCommands[commandId];
  }

  return {
    ...state,
    envelope,
    pendingOperations: state.pendingOperations.filter((operation) => {
      if (!operationBelongsOnEnvelope(operation, envelope)) return false;
      if (acknowledgedOperationIds.has(operation.operationId)) return false;
      if (operation.kind !== "ResolutionDecision") return true;
      return envelope.resolutionStates.some((resolutionState) =>
        proposalResolutionDecisionTargetsState(operation, resolutionState),
      );
    }),
    inFlightCommands,
    conflicts: envelope.conflicts,
    syncStatus: Object.keys(inFlightCommands).length > 0 ? "saving" : "idle",
  };
}

/**
 * Holds a client-team roster the viewer has edited, so the explicit Save has something to write.
 *
 * Staging a roster that matches the one it was edited against is not an edit, so it clears the
 * draft rather than recording an empty one: the editor must not read as dirty because somebody
 * added a member and then took them off again.
 */
export function stageProposalClientTeamRoster(
  state: ProposalCollaborationState,
  draft: ProposalClientTeamRosterDraft,
): ProposalCollaborationState {
  if (proposalClientTeamRostersAreEqual(draft.members, draft.baseline)) {
    return state.pendingClientTeamMembers ? { ...state, pendingClientTeamMembers: undefined } : state;
  }
  return {
    ...state,
    pendingClientTeamMembers: {
      members: normalizeProposalClientTeamRoster(draft.members),
      baseline: normalizeProposalClientTeamRoster(draft.baseline),
    },
  };
}

/**
 * Moves a staged roster past the roster a `client-team-members` write has just acknowledged.
 *
 * A write acknowledges the roster it carried and nothing else. The roster controls stay usable
 * while that request is in flight, so the viewer can add or remove somebody after it left and
 * before it lands; dropping the draft wholesale on success would throw that edit away without
 * saying so (#5276). Instead the draft is re-expressed against what was written and its merge base
 * advanced to it — the rule `receiveProposalEnvelope` already applies to the operation queue, which
 * retires only the operation ids the server named and keeps whatever was queued behind them.
 *
 * In the ordinary case — nothing arrived during the flight — the rebased roster is exactly the
 * written one, so `stageProposalClientTeamRoster` clears the draft and the page goes clean. Where a
 * second edit did arrive, what survives is that edit alone: the page stays dirty over the part that
 * has not been written, and its merge base is the roster the server now holds, so the tab does not
 * report the viewer's own save as somebody else moving the roster underneath them.
 */
export function settleProposalClientTeamRoster(
  state: ProposalCollaborationState,
  writtenMembers: readonly ProposalClientTeamMemberReference[],
): ProposalCollaborationState {
  const draft = state.pendingClientTeamMembers;
  if (!draft) return state;
  const written = normalizeProposalClientTeamRoster(writtenMembers);
  return stageProposalClientTeamRoster(state, {
    members: rebaseProposalClientTeamRoster(draft, written),
    baseline: written,
  });
}

/**
 * Records conflicts reported by a rejected lifecycle command (submit, approve,
 * reject, cancel, hire, complete-review, request-revision).
 *
 * Those commands own no in-flight save entry, so `failProposalSave` would
 * no-op on them. This deliberately touches nothing but `conflicts`: the
 * pending-operation queue, the in-flight command map and `syncStatus` all
 * describe the *save* pipeline, and a rejected lifecycle command neither
 * queued nor lost any local edit.
 *
 * `receiveProposalEnvelope` already copies `envelope.conflicts`, so this is a
 * backstop for the one case it cannot cover: a 409 envelope that loses the
 * `streamVersion` guard to a newer envelope delivered by realtime while the
 * command was in flight. The conflicts are still real; only the envelope is stale.
 */
export function recordProposalConflicts(
  state: ProposalCollaborationState,
  conflicts: readonly ProposalConflict[],
): ProposalCollaborationState {
  return { ...state, conflicts: [...conflicts] };
}

export function queueProposalReviewerChange(
  state: ProposalCollaborationState,
  reviewerChange: ProposalReviewerChange,
): ProposalCollaborationState {
  const reviewerHasInFlightChange = Object.values(state.inFlightCommands).some((command) =>
    command?.reviewerChanges.some((change) => reviewerIdentity(change) === reviewerIdentity(reviewerChange)),
  );

  return {
    ...state,
    pendingReviewerChanges: composePendingReviewerChanges(state.pendingReviewerChanges, reviewerChange),
    syncStatus: reviewerHasInFlightChange ? "saving" : state.syncStatus,
  };
}

export function queueProposalOperation(
  state: ProposalCollaborationState,
  operation: ProposalContentOperation,
): ProposalCollaborationState {
  if (!operationBelongsOnEnvelope(operation, state.envelope)) return state;

  const operationAlreadyQueued = [
    ...state.pendingOperations,
    ...Object.values(state.inFlightCommands).flatMap((command) => command?.operations ?? []),
  ].some((queuedOperation) => queuedOperation.operationId === operation.operationId);

  if (operationAlreadyQueued) return state;

  const existingPendingIndex = state.pendingOperations.findIndex(
    (pendingOperation) => operationQueueKey(pendingOperation) === operationQueueKey(operation),
  );
  if (existingPendingIndex !== -1) {
    const existingPendingOperation = state.pendingOperations[existingPendingIndex];
    const composedOperation = composeContentOperations(existingPendingOperation, operation);
    const pendingOperations = state.pendingOperations.filter((_, index) => index !== existingPendingIndex);
    return {
      ...state,
      pendingOperations: composedOperation
        ? [...pendingOperations.slice(0, existingPendingIndex), composedOperation, ...pendingOperations.slice(existingPendingIndex)]
        : pendingOperations,
    };
  }

  return {
    ...state,
    pendingOperations: [...state.pendingOperations, operation],
  };
}

function operationBelongsOnEnvelope(
  operation: ProposalContentOperation,
  envelope: ProposalCollaborationEnvelope | undefined,
): boolean {
  if (!envelope || envelope.projection.status === "AwaitingSubmission") return true;
  return !isSubmittedLifecycleSetupPath(operation.path);
}

function isSubmittedLifecycleSetupPath(path: string): boolean {
  return path === "/negotiable" ||
    path === "/clientTeam/isMasked" ||
    path === "/clientTeam/leader" ||
    path === "/clientTeam/marketplaceId" ||
    path === "/vendorTeam/isMasked" ||
    path === "/vendorTeam/leader" ||
    path === "/vendorTeam/marketplaceId";
}

/**
 * Replaces pending content expectations after a counterparty supersession.
 * This is deliberately separate from ordinary edit composition: A→B→C edits
 * retain expected A, while a new authoritative incoming target must become the
 * expectation for an explicitly preserved local alternative.
 */
export function rebasePendingProposalContentOperations(
  state: ProposalCollaborationState,
  conflictKeys: readonly string[],
  operations: readonly Exclude<ProposalContentOperation, { kind: "ResolutionDecision" }>[],
): ProposalCollaborationState {
  const keys = [...new Set(conflictKeys)];
  if (keys.length === 0) return state;
  const keyMatches = (operation: ProposalContentOperation) =>
    keys.some((key) => proposalConflictKeyContains(key, operation.conflictKey));

  const firstReplacementIndex = state.pendingOperations.findIndex(
    (operation) => operation.kind !== "ResolutionDecision" && keyMatches(operation),
  );
  if (firstReplacementIndex === -1) return state;

  const remaining = state.pendingOperations.filter(
    (operation) => operation.kind === "ResolutionDecision" || !keyMatches(operation),
  );
  const insertionIndex = Math.min(firstReplacementIndex, remaining.length);
  return {
    ...state,
    pendingOperations: [
      ...remaining.slice(0, insertionIndex),
      ...operations,
      ...remaining.slice(insertionIndex),
    ],
  };
}

export function startProposalSave(
  state: ProposalCollaborationState,
  command: SaveProposalDraftCommand,
): ProposalCollaborationState {
  if (state.inFlightCommands[command.commandId]) return state;

  const operationIds = new Set(command.operations.map((operation) => operation.operationId));
  const reviewerChangeKeys = new Set(command.reviewerChanges.map(reviewerChangeKey));
  return {
    ...state,
    pendingOperations: state.pendingOperations.filter((operation) => !operationIds.has(operation.operationId)),
    pendingReviewerChanges: state.pendingReviewerChanges.filter(
      (reviewerChange) => !reviewerChangeKeys.has(reviewerChangeKey(reviewerChange)),
    ),
    inFlightCommands: {
      ...state.inFlightCommands,
      [command.commandId]: command,
    },
    syncStatus: "saving",
  };
}

export function failProposalSave(
  state: ProposalCollaborationState,
  commandId: string,
  options: { uncertain?: boolean; conflicts?: ProposalConflict[] } = {},
): ProposalCollaborationState {
  const command = state.inFlightCommands[commandId];
  if (!command) return state;

  if (options.uncertain) {
    return {
      ...state,
      conflicts: options.conflicts ?? state.conflicts,
      syncStatus: "resyncRequired",
    };
  }

  const inFlightCommands = { ...state.inFlightCommands };
  delete inFlightCommands[commandId];
  let restoredOperationState = createProposalCollaborationState({
    ...state,
    pendingOperations: [],
    inFlightCommands,
  });
  for (const operation of [...command.operations, ...state.pendingOperations]) {
    restoredOperationState = queueProposalOperation(restoredOperationState, operation);
  }
  const pendingReviewerIds = new Set(state.pendingReviewerChanges.map(reviewerChangeKey));
  const restoredReviewerChanges = command.reviewerChanges.filter(
    (change) => !pendingReviewerIds.has(reviewerChangeKey(change)),
  );

  return {
    ...state,
    pendingOperations: restoredOperationState.pendingOperations,
    pendingReviewerChanges: [...restoredReviewerChanges, ...state.pendingReviewerChanges],
    inFlightCommands,
    conflicts: options.conflicts ?? state.conflicts,
    syncStatus: "error",
  };
}

function reviewerChangeKey(change: ProposalReviewerChange): string {
  return `${reviewerChangeAspect(change)}:${reviewerIdentity(change)}`;
}

function reviewerIdentity(change: ProposalReviewerChange): string {
  return `${change.reviewerUserId}:${change.reviewerLegalEntityId}`;
}

function composeContentOperations(
  existing: ProposalContentOperation,
  next: ProposalContentOperation,
): ProposalContentOperation | undefined {
  if (existing.kind === "ResolutionDecision" && next.kind === "ResolutionDecision") {
    if (!resolutionTargetsEqual(existing, next)) return next;
    return existing.expectedValue === next.value
      ? undefined
      : { ...existing, value: next.value };
  }
  if (existing.kind === "FieldSet" && next.kind === "FieldSet") {
    return proposalStrictJsonValuesEqual(existing.expectedValue, next.value)
      ? undefined
      : { ...existing, value: next.value };
  }
  if (existing.kind === "CollectionItemAdded" && next.kind === "CollectionItemUpdated") {
    return { ...existing, value: mergeJson(existing.value, next.value) as never };
  }
  if (existing.kind === "CollectionItemAdded" && next.kind === "CollectionItemRemoved") return undefined;
  // The mirror of the rule above, and its absence was a live defect. Removing a SAVED collection item
  // and then undoing that removal re-adds it, which diffs as `CollectionItemAdded` and — with no rule
  // here — fell through to `return next`, leaving a pending "add" for an item the server already holds.
  // That op is invisible: the projector's add is a no-op when the item is already there, so the editor
  // and every badge read correctly. It only surfaces later, when a GENUINE removal of that same item
  // arrives on the same conflict key and hits the add/remove annihilation above — which drops both, so
  // the removal never reaches the queue and the item silently stays on the proposal.
  //
  // Found through Replace, which is one add plus one remove: replacing a conflicts document the user
  // had earlier removed-and-undone left the proposal holding BOTH the original and its replacement. The
  // same sequence without Replace — remove, undo, remove again — makes the second removal do nothing at
  // all. Symmetrical with the `FieldSet` rule above: an edit that returns a value to where it started
  // is not a change to save.
  //
  // Compared CANONICALLY, not as the two values happen to be shaped. A document collection is the one
  // place the builder deliberately makes them differ: a removal's `expectedValue` is the canonical item
  // the ledger carries (`{documentId, replacesId}`), while an add's `value` is seeded from the RAW item
  // so the local projection still has the `documentInfo` blob the deserializer needs to render a name
  // before any round trip (`buildProposalCollectionOperations`, and the wire boundary re-canonicalizes
  // it). A strict comparison of the two therefore never matched on exactly the collection this defect
  // lives in.
  if (existing.kind === "CollectionItemRemoved" && next.kind === "CollectionItemAdded") {
    return proposalStrictJsonValuesEqual(
      canonicalProposalCollectionItem(existing.path, existing.expectedValue),
      canonicalProposalCollectionItem(next.path, next.value),
    )
      ? undefined
      : next;
  }
  if (existing.kind === "CollectionItemUpdated" && next.kind === "CollectionItemUpdated") {
    const value = mergeJson(existing.value, next.value);
    return proposalStrictJsonValuesEqual(existing.expectedValue, value)
      ? undefined
      : { ...existing, value: value as never };
  }
  return next;
}

/** Content and decision operations intentionally share a conflict key in one command. */
function operationQueueKey(operation: ProposalContentOperation): string {
  const operationClass = operation.kind === "ResolutionDecision" ? "resolution" : "content";
  return `${operationClass}:${operation.conflictKey}`;
}

function resolutionTargetsEqual(
  existing: Extract<ProposalContentOperation, { kind: "ResolutionDecision" }>,
  next: Extract<ProposalContentOperation, { kind: "ResolutionDecision" }>,
): boolean {
  const existingHasTarget = Object.prototype.hasOwnProperty.call(existing, "targetValue");
  const nextHasTarget = Object.prototype.hasOwnProperty.call(next, "targetValue");
  return (
    existing.path === next.path &&
    existing.itemId === next.itemId &&
    existingHasTarget === nextHasTarget &&
    (!existingHasTarget || proposalStrictJsonValuesEqual(existing.targetValue, next.targetValue))
  );
}

function composePendingReviewerChanges(
  pending: ProposalReviewerChange[],
  next: ProposalReviewerChange,
): ProposalReviewerChange[] {
  const identity = reviewerIdentity(next);
  const unrelated = pending.filter((change) => reviewerIdentity(change) !== identity);
  const existing = pending.filter((change) => reviewerIdentity(change) === identity);
  const assignment = existing.find((change) => change.kind === "Assign");
  const removal = existing.find((change) => change.kind === "Remove");

  if (next.kind === "SetCanEdit") {
    if (assignment?.kind === "Assign") {
      return [...unrelated, ...existing.filter((change) => change !== assignment), { ...assignment, canEdit: next.canEdit }];
    }
    const existingAccess = existing.find((change) => change.kind === "SetCanEdit");
    const otherAspects = existing.filter((change) => reviewerChangeAspect(change) !== "access");
    const expectedCanEdit = existingAccess?.kind === "SetCanEdit" ? existingAccess.expectedCanEdit : next.expectedCanEdit;
    return expectedCanEdit === next.canEdit
      ? [...unrelated, ...otherAspects]
      : [...unrelated, ...otherAspects, { ...next, expectedCanEdit }];
  }

  if (next.kind === "SetCustomMessage") {
    if (assignment?.kind === "Assign") {
      return [
        ...unrelated,
        ...existing.filter((change) => change !== assignment),
        { ...assignment, customMessage: next.customMessage },
      ];
    }
    const existingMessage = existing.find((change) => change.kind === "SetCustomMessage");
    const otherAspects = existing.filter((change) => reviewerChangeAspect(change) !== "message");
    const expectedCustomMessage =
      existingMessage?.kind === "SetCustomMessage"
        ? existingMessage.expectedCustomMessage
        : next.expectedCustomMessage;
    return proposalStrictJsonValuesEqual(expectedCustomMessage, next.customMessage)
      ? [...unrelated, ...otherAspects]
      : [...unrelated, ...otherAspects, { ...next, expectedCustomMessage }];
  }

  if (next.kind === "Remove") {
    if (assignment) return unrelated;
    const accessChange = existing.find((change) => change.kind === "SetCanEdit");
    const messageChange = existing.find((change) => change.kind === "SetCustomMessage");
    return [
      ...unrelated,
      {
        ...next,
        expectedCanEdit: accessChange?.kind === "SetCanEdit" ? accessChange.expectedCanEdit : next.expectedCanEdit,
        expectedCustomMessage:
          messageChange?.kind === "SetCustomMessage"
            ? messageChange.expectedCustomMessage
            : next.expectedCustomMessage,
      },
    ];
  }

  if (removal?.kind === "Remove") {
    const restored: ProposalReviewerChange[] = [];
    if (removal.expectedCanEdit !== next.canEdit) {
      restored.push({
        kind: "SetCanEdit",
        reviewerUserId: next.reviewerUserId,
        reviewerLegalEntityId: next.reviewerLegalEntityId,
        expectedCanEdit: removal.expectedCanEdit,
        canEdit: next.canEdit,
      });
    }
    if (!proposalStrictJsonValuesEqual(removal.expectedCustomMessage, next.customMessage)) {
      restored.push({
        kind: "SetCustomMessage",
        reviewerUserId: next.reviewerUserId,
        reviewerLegalEntityId: next.reviewerLegalEntityId,
        expectedCustomMessage: removal.expectedCustomMessage,
        customMessage: next.customMessage,
      });
    }
    return [...unrelated, ...restored];
  }

  return [...unrelated, ...existing, next];
}

function reviewerChangeAspect(change: ProposalReviewerChange): "membership" | "access" | "message" {
  if (change.kind === "SetCanEdit") return "access";
  if (change.kind === "SetCustomMessage") return "message";
  return "membership";
}

function mergeJson(left: unknown, right: unknown): unknown {
  if (!isRecord(left) || !isRecord(right)) return structuredClone(right);
  const merged = structuredClone(left);
  for (const [key, value] of Object.entries(right)) {
    merged[key] = isRecord(merged[key]) && isRecord(value) ? mergeJson(merged[key], value) : structuredClone(value);
  }
  return merged;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
